import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { RootState } from 'src/app/store/store';
import { fetchFilterDocuments, fetchRemainingDocuments } from '../redux';
import { WikiDocument } from 'src/lib/types';

interface UseFilterDocumentsParams {
  categories: string[];
  domains: string[];
  subdomains: string[];
  search: string;
  enabled: boolean;
}

interface UseFilterDocumentsReturn {
  documents: Array<WikiDocument & { disabled?: boolean }>;
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
}

export const useFilterDocuments = (params: UseFilterDocumentsParams): UseFilterDocumentsReturn => {
  const dispatch = useAppDispatch();
  const filterDocuments = useAppSelector((state: RootState) => state.chat.filterDocuments);
  const loadingRef = useRef(false);

  // Keep ref in sync so callbacks always see the latest value
  loadingRef.current = filterDocuments.isLoading;

  // Reset and fetch page 1 whenever filters or search change, but only when enabled
  useEffect(() => {
    if (!params.enabled) return;

    dispatch(
      fetchFilterDocuments({
        categories: params.categories,
        domains: params.domains,
        subdomains: params.subdomains,
        search: params.search,
        page: 1,
        append: false
      })
    );
  }, [dispatch, params.enabled, params.categories, params.domains, params.subdomains, params.search]);

  const hasActiveFilters = params.categories.length > 0 || params.domains.length > 0 || params.subdomains.length > 0;
  const hasMatchingPages = filterDocuments.matchingPage < filterDocuments.matchingTotalPages;
  // Remaining starts at page=0/totalPages=0; treat as "has more" until the first fetch proves otherwise
  const remainingNotYetFetched =
    hasActiveFilters && filterDocuments.remainingPage === 0 && filterDocuments.remainingTotalPages === 0;
  const hasRemainingPages =
    filterDocuments.remainingPage < filterDocuments.remainingTotalPages || remainingNotYetFetched;
  const hasMore = hasMatchingPages || hasRemainingPages;

  // Use refs for values checked inside loadMore so the callback identity stays stable
  const hasMatchingRef = useRef(hasMatchingPages);
  const hasRemainingRef = useRef(hasRemainingPages);
  const matchingPageRef = useRef(filterDocuments.matchingPage);
  const remainingPageRef = useRef(filterDocuments.remainingPage);
  hasMatchingRef.current = hasMatchingPages;
  hasRemainingRef.current = hasRemainingPages;
  matchingPageRef.current = filterDocuments.matchingPage;
  remainingPageRef.current = filterDocuments.remainingPage;

  // Stable loadMore — only depends on dispatch + params (which change on filter/search reset)
  const loadMore = useCallback(() => {
    if (loadingRef.current || (!hasMatchingRef.current && !hasRemainingRef.current)) {
      return;
    }

    if (hasMatchingRef.current) {
      dispatch(
        fetchFilterDocuments({
          categories: params.categories,
          domains: params.domains,
          subdomains: params.subdomains,
          search: params.search,
          page: matchingPageRef.current + 1,
          append: true
        })
      );
    } else if (hasRemainingRef.current) {
      dispatch(
        fetchRemainingDocuments({
          categories: params.categories,
          domains: params.domains,
          subdomains: params.subdomains,
          search: params.search,
          page: remainingPageRef.current + 1,
          append: true
        })
      );
    }
  }, [dispatch, params.categories, params.domains, params.subdomains, params.search]);

  // Combine documents: matching (enabled) first, then remaining (disabled)
  // Deduplicate: remove docs from remaining that already appear in matching
  const combinedDocuments = useMemo(() => {
    const matchingIds = new Set(filterDocuments.matching.map((d) => d.id));
    const dedupedRemaining = filterDocuments.remaining.filter((d) => !matchingIds.has(d.id));

    return [
      ...filterDocuments.matching.map((d) => ({ ...d, disabled: false })),
      ...dedupedRemaining.map((d) => ({ ...d, disabled: true }))
    ];
  }, [filterDocuments.matching, filterDocuments.remaining]);

  // Check if currently loading the initial page
  const isInitialLoad = filterDocuments.isLoading && filterDocuments.matchingPage === 0;

  // Check if loading more pages
  const isLoadingMore = filterDocuments.isLoading && filterDocuments.matchingPage > 0;

  return {
    documents: combinedDocuments,
    loadMore,
    hasMore,
    isLoading: isInitialLoad,
    isLoadingMore
  };
};
